/* eslint-disable arrow-parens */
// Visual builder for a group's Digital ID Card config — brand/style as real
// fields, card variants as tabs, and each variant's front/back content as a
// recursive section-tree editor (SectionListEditor) instead of hand-edited
// JSON. Load/save/preview wiring is unchanged from the earlier MVP pass.
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
	Button,
	Grid,
	Paper,
	TextField,
	Typography,
	CircularProgress,
	LinearProgress,
	Tabs,
	Tab,
	IconButton,
	Checkbox,
	FormControlLabel
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import PublishIcon from "@material-ui/icons/Publish";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { trackPromise } from "react-promise-tracker";

import { AuthContext, UIContext } from "../../../../contexts";
import { api } from "../../../../utils/api";
import useRouteParam from "../../../../hooks/useRouteParam";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import { getPermissionsFromGroupOwner } from "../../../../utils/getPermissionsFromGroupOwner";
import { SectionListEditor } from "./SectionEditor";
import StringListEditor from "./StringListEditor";
import {
	CardStyle,
	HealthCardAssetKeysResult,
	HealthCardConfigPayload,
	HealthCardVariant
} from "../../../../@types/healthCardConfig.types";

// White (not a brand-color guess) for a group with no saved config yet —
// the .hc-card's own background is already white, so a white gradient is
// effectively a no-op until the group's real brand colors are set.
const DEFAULT_STYLE: CardStyle = {
	gradient_start: "#FFFFFF",
	gradient_end: "#FFFFFF",
	text_color: "#1c2b3a"
};

const defaultVariant = (index: number): HealthCardVariant => ({
	key: `variant_${index + 1}`,
	label: `Variant ${index + 1}`,
	eligible_plan_names: [],
	front: [],
	back: []
});

// QR Foreground/Background used to live here too, but real per-member QR
// generation was never wired up — every preview uses the same static
// placeholder QR image, so those two colors did nothing. Removed rather
// than kept-but-inert; re-add once QR generation actually consumes them.
// accent_color was removed for the same reason (QR stays the same static
// placeholder image, so there was nothing left for it to color) — the QR
// corner-bracket decoration it used to drive now just inherits text_color.
const STYLE_FIELDS: { key: keyof CardStyle; label: string }[] = [
	{ key: "gradient_start", label: "Gradient Start" },
	{ key: "gradient_end", label: "Gradient End" },
	{ key: "text_color", label: "Text Color" }
];

const HealthCardConfig = () => {
	const groupNumber = useRouteParam("gn") as number | null;
	const { user } = useContext(AuthContext);
	const { setDashboardHeader } = useContext(UIContext);

	const [config, setConfig] = useState<HealthCardConfigPayload | null>(null);
	const [assetKeys, setAssetKeys] = useState<HealthCardAssetKeysResult | undefined>();
	const [loading, setLoading] = useState(false);
	const [activeVariantIndex, setActiveVariantIndex] = useState(0);
	// Which face the Front/Back tabs show — shared by the section editor and
	// the live preview (which hides the other face's .hc-card via injected
	// CSS, see displayedPreviewHtml below) so they always stay in sync.
	const [activeFace, setActiveFace] = useState<"front" | "back">("front");
	const [previewHtml, setPreviewHtml] = useState<string | undefined>();
	const [previewLoading, setPreviewLoading] = useState(false);
	const previewTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const permission = user
		? getPermissionsFromGroupOwner(user, Number(groupNumber))?.employer_settings_menu?.digital_id_card_sub?.self
		: null;

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.digital_id_card);
	}, [setDashboardHeader]);

	// Native HTML5 drag-and-drop doesn't auto-scroll the page when the
	// cursor nears the top/bottom edge while dragging a section — with a
	// long list of sections, the row you actually want to drop onto can be
	// scrolled off-screen entirely. Listening at the document level (rather
	// than inside SectionEditor's own drag handlers) means one listener
	// covers every SectionListEditor instance on the page (front, back, and
	// any nested two_column sides) instead of each needing its own.
	useEffect(() => {
		const EDGE_SIZE = 80; // px from the viewport edge that starts scrolling
		const MAX_SPEED = 20; // px scrolled per dragover at the very edge
		const handleDragOver = (event: DragEvent) => {
			const { clientY } = event;
			const viewportHeight = window.innerHeight;
			if (clientY < EDGE_SIZE) {
				window.scrollBy(0, -MAX_SPEED * ((EDGE_SIZE - clientY) / EDGE_SIZE));
			} else if (clientY > viewportHeight - EDGE_SIZE) {
				window.scrollBy(0, MAX_SPEED * ((clientY - (viewportHeight - EDGE_SIZE)) / EDGE_SIZE));
			}
		};
		document.addEventListener("dragover", handleDragOver);
		return () => document.removeEventListener("dragover", handleDragOver);
	}, []);

	const loadConfig = useCallback(async () => {
		if (!groupNumber) return;
		setLoading(true);
		try {
			const results = await trackPromise(
				api.healthCardConfig.getHealthCardConfigs({ group_number: groupNumber })
			);
			const existing = results?.[0];
			// A group whose config was saved before a style field was removed
			// (e.g. the old accent_color) can still have that field sitting in
			// its stored document — rebuild `style` from only the fields
			// CardStyle currently declares so it doesn't ride along in memory
			// and get sent straight back out on the next Save (the backend's
			// style validator rejects unknown keys, so an unstripped legacy
			// field would make Save fail outright, not just look odd in the UI).
			const next: HealthCardConfigPayload = existing
				? {
						...existing,
						style: {
							gradient_start: existing.style.gradient_start,
							gradient_end: existing.style.gradient_end,
							text_color: existing.style.text_color
						}
				  }
				: {
						group_number: groupNumber,
						brand_name: "",
						style: DEFAULT_STYLE,
						cards: [defaultVariant(0)]
				  };
			setConfig(next);
			setActiveVariantIndex(0);
		} finally {
			setLoading(false);
		}
	}, [groupNumber]);

	const loadAssetKeys = useCallback(async () => {
		if (!groupNumber) return;
		const result = await api.healthCardConfig.getHealthCardAssetKeys(groupNumber);
		setAssetKeys(result);
	}, [groupNumber]);

	useEffect(() => {
		loadConfig();
		loadAssetKeys();
	}, [loadConfig, loadAssetKeys]);

	const handleBrandNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setConfig((prev) => (prev ? { ...prev, brand_name: event.target.value } : prev));
	};

	const handleStyleChange = (key: keyof CardStyle) => (event: React.ChangeEvent<HTMLInputElement>) => {
		setConfig((prev) => (prev ? { ...prev, style: { ...prev.style, [key]: event.target.value } } : prev));
	};

	const updateVariant = (index: number, patch: Partial<HealthCardVariant>) => {
		setConfig((prev) => {
			if (!prev) return prev;
			const cards = prev.cards.map((card, i) => (i === index ? { ...card, ...patch } : card));
			return { ...prev, cards };
		});
	};

	const addVariant = () => {
		setConfig((prev) => {
			if (!prev) return prev;
			const cards = [...prev.cards, defaultVariant(prev.cards.length)];
			setActiveVariantIndex(cards.length - 1);
			return { ...prev, cards };
		});
	};

	const removeVariant = (index: number) => {
		setConfig((prev) => {
			if (!prev || prev.cards.length <= 1) return prev;
			const cards = prev.cards.filter((_, i) => i !== index);
			setActiveVariantIndex((current) => Math.min(current, cards.length - 1));
			return { ...prev, cards };
		});
	};

	const toggleHasBack = (index: number, hasBack: boolean) => {
		updateVariant(index, { back: hasBack ? [] : undefined });
	};

	const handleSave = async () => {
		if (!config || !groupNumber) return;
		const payload: HealthCardConfigPayload = { ...config, group_number: groupNumber };
		const saved = config._id
			? await trackPromise(api.healthCardConfig.editHealthCardConfig(payload))
			: await trackPromise(api.healthCardConfig.addHealthCardConfig(payload));
		if (saved) setConfig(saved);
	};

	// Click-to-jump: the preview iframe's content is stamped with the same
	// front/back-then-array-index data-hc-path addressing SectionListEditor
	// puts on its own Paper elements (see buildHealthCardHtml.service.ts's
	// renderSection/renderFace). Clicking a section in the rendered card
	// finds the matching editor block by that shared path and scrolls to
	// it. Whole-section granularity only — no sub-field targeting. Runs on
	// every iframe reload (each debounced preview replaces srcDoc with a
	// brand-new document, so listeners never stack across reloads).
	const handlePreviewLoad = useCallback(() => {
		const iframeDoc = iframeRef.current?.contentDocument;
		if (!iframeDoc) return;
		iframeDoc.addEventListener("click", (event) => {
			const clickedPathEl = (event.target as HTMLElement).closest("[data-hc-path]");
			const path = clickedPathEl?.getAttribute("data-hc-path");
			if (!path) return;
			const formEl = document.querySelector<HTMLElement>(`[data-hc-path="${path}"]`);
			if (!formEl) return;
			formEl.scrollIntoView({ behavior: "smooth", block: "center" });
			const previousBoxShadow = formEl.style.boxShadow;
			const previousTransition = formEl.style.transition;
			formEl.style.transition = "box-shadow 0.2s ease";
			formEl.style.boxShadow = "0 0 0 3px #1976d2";
			setTimeout(() => {
				formEl.style.boxShadow = previousBoxShadow;
				formEl.style.transition = previousTransition;
			}, 1500);
		});
	}, []);

	// Live inline preview (matches the "the render IS the feedback" feel of
	// the Plan Branding page) instead of a manual Preview button that opens
	// a new tab — debounced so it doesn't fire a server round-trip on every
	// keystroke, and deliberately NOT wrapped in trackPromise, since that
	// drives this app's global loading overlay and would flash it on every
	// edit rather than just on real Save/Load actions.
	useEffect(() => {
		if (!config || !groupNumber) {
			setPreviewHtml(undefined);
			return;
		}
		const activeVariantForPreview = config.cards[activeVariantIndex];
		if (!activeVariantForPreview) return;

		if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
		setPreviewLoading(true);
		previewTimeoutRef.current = setTimeout(async () => {
			try {
				const html = await api.healthCardConfig.previewHealthCardConfig({
					config: { ...config, group_number: groupNumber },
					variant_key: activeVariantForPreview.key
				});
				if (html) setPreviewHtml(html);
			} finally {
				setPreviewLoading(false);
			}
		}, 600);

		return () => {
			if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [config, groupNumber, activeVariantIndex]);

	// A different variant's back side may not exist yet — land back on Front
	// rather than staying on a tab that might now be empty/irrelevant.
	useEffect(() => {
		setActiveFace("front");
	}, [activeVariantIndex]);

	if (!groupNumber) {
		return (
			<Alert severity="error" style={{ margin: 16 }}>
				<AlertTitle>Error</AlertTitle>
				Select a group to edit its Digital ID Card design.
			</Alert>
		);
	}

	if (permission && !permission.view) {
		return <Typography style={{ textAlign: "center", padding: 24 }}>No view permission</Typography>;
	}

	const activeVariant = config?.cards[activeVariantIndex];

	// buildHealthCardHtml renders front and back as two stacked `.hc-card`
	// blocks in one document (front first, back second — see
	// buildHealthCardHtml.service.ts) — the preview shows only the tab
	// currently selected by hiding the other one via injected CSS, rather
	// than re-fetching a face-specific render, so switching tabs is instant.
	// Only applied once the back side actually has content — an empty
	// `back: []` (right after checking "has a back side") produces no
	// second `.hc-card` at all, so there'd be nothing to un-hide.
	const hasBackCard = (activeVariant?.back?.length ?? 0) > 0;
	// The currently-viewed face having zero sections is a real, common
	// in-progress state (a brand-new variant, or before "This card has a
	// back side" has any content yet) — worth its own placeholder rather
	// than showing the rendered .hc-card's bare gradient/shadow box with
	// nothing inside it, which just looks like a blank rectangle.
	const activeFaceSections = activeFace === "front" ? activeVariant?.front : activeVariant?.back;
	const isActiveFaceEmpty = !activeFaceSections || activeFaceSections.length === 0;
	const displayedPreviewHtml =
		previewHtml && hasBackCard
			? previewHtml.replace(
					"</style>",
					`.hc-card:nth-of-type(${activeFace === "front" ? 2 : 1}) { display: none; }\n</style>`
			  )
			: previewHtml;

	return (
		<Grid container spacing={2} style={{ padding: 16, paddingBottom: 80 }}>
			<Grid item xs={12} md={8}>
				<Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
					<Typography variant="h6" gutterBottom>
						Brand
					</Typography>
					<TextField
						label="Brand Name"
						value={config?.brand_name ?? ""}
						onChange={handleBrandNameChange}
						fullWidth
						variant="outlined"
						margin="dense"
						placeholder='e.g. "Brookshire Brothers, Inc."'
					/>
					<Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 4 }}>
						Not shown directly on this card — used for the preview&apos;s document title and the mobile
						app&apos;s simpler ID card screen. For text that appears on the card itself, edit the Header
						section&apos;s Title/Subtitle lines below.
					</Typography>
				</Paper>

				<Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
					<Typography variant="h6" gutterBottom>
						Style
					</Typography>
					<Grid container spacing={2}>
						{STYLE_FIELDS.map(({ key, label }) => (
							<Grid item xs={6} sm={4} key={key}>
								<TextField
									label={label}
									type="color"
									value={config?.style[key] ?? "#000000"}
									onChange={handleStyleChange(key)}
									fullWidth
									variant="outlined"
									margin="dense"
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>
						))}
					</Grid>
				</Paper>

				<Paper variant="outlined" style={{ padding: 16 }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<Typography variant="h6">Card Variants</Typography>
						<Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={addVariant}>
							Add Variant
						</Button>
					</div>
					<Tabs
						value={activeVariantIndex}
						onChange={(_event, value) => setActiveVariantIndex(value)}
						variant="scrollable"
						scrollButtons="auto"
					>
						{(config?.cards ?? []).map((card, index) => (
							<Tab key={card.key || index} label={card.label || card.key || `Variant ${index + 1}`} />
						))}
					</Tabs>

					{activeVariant && (
						<div style={{ marginTop: 16 }}>
							<div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
								<TextField
									label="Key"
									value={activeVariant.key}
									onChange={(event) => updateVariant(activeVariantIndex, { key: event.target.value })}
									variant="outlined"
									margin="dense"
									style={{ flex: 1 }}
									helperText='Internal id, e.g. "health", "dental", "clinic"'
								/>
								<TextField
									label="Label"
									value={activeVariant.label}
									onChange={(event) =>
										updateVariant(activeVariantIndex, { label: event.target.value })
									}
									variant="outlined"
									margin="dense"
									style={{ flex: 1 }}
									helperText="Shown in a toggle if a group has multiple variants"
								/>
								{(config?.cards.length ?? 0) > 1 && (
									<IconButton
										size="small"
										onClick={() => removeVariant(activeVariantIndex)}
										aria-label="Remove variant"
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								)}
							</div>

							<StringListEditor
								label="Eligible plan name"
								values={activeVariant.eligible_plan_names}
								onChange={(eligible_plan_names) =>
									updateVariant(activeVariantIndex, { eligible_plan_names })
								}
								placeholder='Must match an enrollment plan_name exactly, e.g. "Health Plan"'
							/>

							<Tabs
								value={activeFace}
								onChange={(_event, value) => setActiveFace(value)}
								style={{ margin: 16 }}
								indicatorColor="primary"
								textColor="primary"
							>
								<Tab value="front" label="Front" />
								<Tab value="back" label="Back" />
							</Tabs>

							{activeFace === "front" && (
								<SectionListEditor
									sections={activeVariant.front}
									onChange={(front) => updateVariant(activeVariantIndex, { front })}
									assetKeys={assetKeys}
									path="front"
								/>
							)}

							{activeFace === "back" && (
								<>
									<FormControlLabel
										control={
											<Checkbox
												checked={activeVariant.back !== undefined}
												onChange={(event) =>
													toggleHasBack(activeVariantIndex, event.target.checked)
												}
											/>
										}
										label="This card has a back side"
										style={{ marginTop: 8 }}
									/>
									{activeVariant.back !== undefined && (
										<SectionListEditor
											sections={activeVariant.back}
											onChange={(back) => updateVariant(activeVariantIndex, { back })}
											assetKeys={assetKeys}
											path="back"
										/>
									)}
								</>
							)}
						</div>
					)}
				</Paper>
			</Grid>

			<Grid item xs={12} md={4}>
				<Paper variant="outlined" style={{ padding: 16, position: "sticky", top: 16 }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<Typography variant="h6">
							Live Preview {hasBackCard ? `— ${activeFace === "front" ? "Front" : "Back"}` : ""}
						</Typography>
						{previewLoading && <CircularProgress size={16} />}
					</div>
					{previewLoading && <LinearProgress style={{ marginBottom: 8 }} />}
					{isActiveFaceEmpty ? (
						<div
							style={{
								background: "#fff",
								border: "1px dashed #ccc",
								borderRadius: 8,
								padding: 48,
								textAlign: "center"
							}}
						>
							<Typography variant="body2" color="textSecondary">
								Add blocks to see the {activeFace} of the card here.
							</Typography>
						</div>
					) : displayedPreviewHtml ? (
						<iframe
							ref={iframeRef}
							srcDoc={displayedPreviewHtml}
							onLoad={handlePreviewLoad}
							title="Digital ID Card preview"
							style={{ width: "100%", height: 400, border: "none" }}
						/>
					) : (
						<Typography variant="body2" color="textSecondary" style={{ padding: 24, textAlign: "center" }}>
							Start editing to see the card here.
						</Typography>
					)}
				</Paper>
			</Grid>

			{permission && permission.update && (
				<div style={{ position: "fixed", bottom: 24, right: 24, display: "flex", gap: 8 }}>
					<Button
						variant="contained"
						color="primary"
						startIcon={<PublishIcon />}
						onClick={handleSave}
						disabled={loading}
					>
						{config?._id ? "Save" : "Publish"}
					</Button>
				</div>
			)}
		</Grid>
	);
};

export default HealthCardConfig;
